import { useState } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { addMonths } from '../engine/civil'
import { validateRecurrenceRule } from '../engine/recurrence'
import type { RecurrenceRule } from '../engine/recurrence'
import type { IsoDate, ShiftCode } from '../engine/types'
import { formatShortDate, formatTimeRange } from '../lib/format-date'
import { ACTAS_ROSTER } from '../data/roster-config'
import { rosterShift } from '../data/roster-shifts'
import type { DeleteRecurrenceResult, RosterDataError, SaveRecurrenceResult } from '../lib/roster-data'
import { Button, SegmentedControl, SelectField, Sheet, TextField } from '../ui'
import type { SegmentedOption, SelectOption } from '../ui'

export interface RecurrenceSheetProps {
  /**
   * The rule being edited, or `null` to create one. The edit path pre-fills
   * every field from it, and saving carries its `id` and its
   * `exceptionDates` through untouched — the regeneration on save honours
   * the array, and losing it would resurrect occurrences the user deleted.
   */
  rule: RecurrenceRule | null
  /**
   * `roster-data.ts`'s `saveRecurrence`, owned by the screen — this sheet
   * composes the rule, validates it against the engine, and hands it over.
   */
  onSave: (rule: RecurrenceRule) => Promise<SaveRecurrenceResult>
  /**
   * `roster-data.ts`'s `deleteRecurrence`, owned by the screen. Edit path
   * only — a rule that does not exist yet has nothing to delete.
   */
  onDelete?: () => Promise<DeleteRecurrenceResult>
  /**
   * Prefill for the create path's start date — the day the sheet was opened
   * from, when it was opened from one. Ignored when editing.
   */
  defaultStartDate?: IsoDate
  /**
   * Today, injected rather than read from the clock — the same seam
   * `CalendarScreen` runs. Anchors the A7 horizon the sheet enforces on the
   * start and end dates in `handleSave`, so a typo'd year cannot save a rule
   * that generates nothing any reachable month will show.
   */
  today: IsoDate
  onClose: () => void
}

const KIND_OPTIONS: SegmentedOption<RecurrenceRule['kind']>[] = [
  { value: 'INTERVAL_DAYS', label: 'Every N days', note: 'e.g. every 3rd day' },
  { value: 'WEEKLY', label: 'Weekly', note: 'e.g. every 2nd Sunday' },
]

/** Sunday-first, 0 = Sunday — the engine's `weekday` convention, not `Date`'s. */
const WEEKDAY_OPTIONS: SelectOption[] = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
]

const WEEKS_INTERVAL_OPTIONS: SelectOption[] = [1, 2, 3, 4, 5, 6].map((weeks) => ({
  value: String(weeks),
  label: weeks === 1 ? 'Every week' : `Every ${weeks} weeks`,
}))

const CODES: readonly ShiftCode[] = ['AM', 'D', 'PM', 'N']

const CODE_OPTIONS: SegmentedOption<ShiftCode>[] = CODES.map((code) => {
  const times = rosterShift(code)
  return { value: code, label: code, note: formatTimeRange(times.startMin, times.endMin) }
})

/**
 * The regeneration-safety guard (`RosterDataError`) as a sentence — the plan's
 * own wording, singular and plural, plus the one clause that keeps it honest
 * in Phase 3: nothing can put a row in that state yet, so the guard reads as
 * the future-proofing it is rather than an error the user must puzzle over.
 */
function guardSentence(guard: RosterDataError): string {
  const count =
    guard.count === 1
      ? "This rule has 1 shift that isn't rostered — withdraw it first."
      : `This rule has ${guard.count} shifts that aren't rostered — withdraw them first.`
  return `${count} Nothing can put a shift in that state yet — this check is here for when advertising arrives in a later phase.`
}

function errorSentence(what: string, error: PostgrestError): string {
  return `Couldn't ${what} — ${error.message}`
}

function skipSentence(skippedCount: number): string {
  // Deliberately not "a shift you added by hand": the skipped date can also
  // carry another rule's generated row (a second, overlapping rule), and the
  // UNIQUE constraint — not the row's origin — is what did the winning.
  return skippedCount === 1
    ? 'Saved — but 1 date this rule generates already has a shift on it, and that shift wins the day.'
    : `Saved — but ${skippedCount} dates this rule generates already have shifts on them, and those shifts win those days.`
}

/**
 * Create or edit a recurrence rule, inside the vendored `Sheet` (plan §1
 * Client). The two kinds carry their own fields — every-N-days gets an
 * interval; weekly gets a weekday (Sunday-first, 0 = Sunday) and an
 * every-n-weeks — and switching kind swaps the visible fields, keeping what
 * was typed in each so a change of mind costs nothing.
 *
 * Save composes a `RecurrenceRule` and runs the engine's own
 * `validateRecurrenceRule` **before** calling `onSave` — the same validator
 * `saveRecurrence` runs first thing — so a malformed rule (an empty interval,
 * a date that isn't a date) never leaves this sheet, and never reaches the
 * path that deletes a rule's rows before regenerating them. The validator
 * throws rather than guesses, so the composition catches its
 * `TypeError`/`RangeError` and renders the message as a sentence.
 *
 * Dates are ISO text fields this phase (`YYYY-MM-DD`), matching the app's
 * `IsoDate`-everywhere convention — no date library, no timezone. An empty
 * end date composes `null`: open-ended, horizon-capped at generation. The
 * horizon (A7) is also enforced here, from the injected `today`: a start or
 * end outside `horizonRange(today)` is refused as a sentence before anything
 * saves — generation alone would happily "succeed" with zero rows no
 * reachable month shows.
 *
 * Every outcome of `saveRecurrence`/`deleteRecurrence` — saved, guard, error,
 * collision skips — renders as a sentence here, and nothing closes over a
 * sentence nobody read. The one deliberate exception is the create path,
 * which closes on success: its draft rule has no `id`, so a still-open sheet
 * would let a second Save insert a second, identical rule, and the screen's
 * refetched "Repeating shifts" list behind the sheet is the confirmation.
 */
export function RecurrenceSheet({
  rule,
  onSave,
  onDelete,
  defaultStartDate,
  today,
  onClose,
}: RecurrenceSheetProps) {
  const [kind, setKind] = useState<RecurrenceRule['kind']>(rule?.kind ?? 'INTERVAL_DAYS')
  const [intervalDaysText, setIntervalDaysText] = useState(
    rule?.intervalDays != null ? String(rule.intervalDays) : '2',
  )
  const [weekdayText, setWeekdayText] = useState(
    rule?.weekday != null ? String(rule.weekday) : '5', // Friday — the brief's own example day.
  )
  const [weeksIntervalText, setWeeksIntervalText] = useState(
    rule?.weeksInterval != null ? String(rule.weeksInterval) : '1',
  )
  const [code, setCode] = useState<ShiftCode>(rule?.shiftCode ?? 'AM')
  const [startDateText, setStartDateText] = useState(
    rule?.startDate ?? defaultStartDate ?? '',
  )
  const [endDateText, setEndDateText] = useState(rule?.endDate ?? '')
  const [status, setStatus] = useState('')

  // One sheet, two jobs: reset the draft when the rule being edited changes,
  // so stale fields from rule A never save onto rule B. (The screen can also
  // force this with `key={rule?.id ?? 'create'}`.)
  const [renderedRuleId, setRenderedRuleId] = useState<string | null>(rule?.id ?? null)
  if ((rule?.id ?? null) !== renderedRuleId) {
    setRenderedRuleId(rule?.id ?? null)
    setKind(rule?.kind ?? 'INTERVAL_DAYS')
    setIntervalDaysText(rule?.intervalDays != null ? String(rule.intervalDays) : '2')
    setWeekdayText(rule?.weekday != null ? String(rule.weekday) : '5')
    setWeeksIntervalText(rule?.weeksInterval != null ? String(rule.weeksInterval) : '1')
    setCode(rule?.shiftCode ?? 'AM')
    setStartDateText(rule?.startDate ?? defaultStartDate ?? '')
    setEndDateText(rule?.endDate ?? '')
    setStatus('')
  }

  function composeRule(): RecurrenceRule {
    const base = {
      shiftCode: code,
      startDate: startDateText.trim(),
      endDate: endDateText.trim() === '' ? null : endDateText.trim(),
      exceptionDates: rule?.exceptionDates ?? [],
    }
    const id = rule?.id != null ? { id: rule.id } : {}
    if (kind === 'INTERVAL_DAYS') {
      // The forbidden partner fields go in as null, not as absent — the
      // database row always carries them and the validator accepts null,
      // rejects a value.
      return { ...id, ...base, kind, intervalDays: Number(intervalDaysText), weekday: null, weeksInterval: null }
    }
    return {
      ...id,
      ...base,
      kind,
      intervalDays: null,
      weekday: Number(weekdayText),
      weeksInterval: Number(weeksIntervalText),
    }
  }

  async function handleSave() {
    const next = composeRule()
    try {
      validateRecurrenceRule(next)
    } catch (error) {
      // A malformed rule never leaves the sheet — and never reaches
      // saveRecurrence, whose own first act is the same validation.
      setStatus(
        `That rule isn't complete yet — ${error instanceof Error ? error.message : String(error)}`,
      )
      return
    }

    // A7's bound, in the sheet: a start (or end) outside the horizon would
    // either generate zero rows before any month the user can reach — saved,
    // then announced as a success — or hundreds of rows no horizon-bounded
    // view ever asked for. The same sentence shape the screen uses for an
    // out-of-horizon day tap; nothing is saved.
    const horizon = {
      from: addMonths(today, -ACTAS_ROSTER.horizon.monthsBack),
      to: addMonths(today, ACTAS_ROSTER.horizon.monthsForward),
    }
    // `composeRule` always sets endDate (null when the field is empty), but
    // the rule type allows undefined, so narrow before comparing.
    const endDate = next.endDate ?? null
    const outOfHorizon =
      next.startDate < horizon.from
        ? next.startDate
        : next.startDate > horizon.to
          ? next.startDate
          : endDate !== null && endDate > horizon.to
            ? endDate
            : null
    if (outOfHorizon !== null) {
      setStatus(
        `${formatShortDate(outOfHorizon)} is outside the range you can edit — days from ${formatShortDate(horizon.from)} to ${formatShortDate(horizon.to)} can be changed.`,
      )
      return
    }

    const result = await onSave(next)
    if (result.error) {
      setStatus(errorSentence('save that rule', result.error))
      return
    }
    if (result.guard) {
      setStatus(guardSentence(result.guard))
      return
    }
    if (rule?.id == null) {
      // Create path: close on success. The rule now exists, but this sheet's
      // draft still composes an id-less rule — a second Save here would insert
      // a second, identical rule. The calendar behind the sheet refetches and
      // lists the new rule, which is the visible confirmation; the edit path
      // stays open because its outcome sentences (guard, skips, delete) are
      // about a rule that remains on screen.
      onClose()
      return
    }
    setStatus(
      result.skippedCount > 0
        ? skipSentence(result.skippedCount)
        : 'Rule saved — its shifts are on your calendar.',
    )
  }

  async function handleDelete() {
    if (onDelete === undefined) return
    const result = await onDelete()
    if (result.error) {
      setStatus(errorSentence('delete that rule', result.error))
      return
    }
    if (result.guard) {
      setStatus(guardSentence(result.guard))
      return
    }
    setStatus('Rule deleted — its generated shifts went with it.')
  }

  const isEdit = rule?.id != null && onDelete !== undefined

  return (
    <Sheet title={isEdit ? 'Edit recurrence' : 'Add recurrence'} onClose={onClose}>
      <div className="sl-stack">
        {/* Exists before it has content, so a screen reader announces the
            sentence when it arrives, not just the first time. */}
        <p className="ss-sheet-status" role="status">
          {status}
        </p>

        <SegmentedControl
          label="Repeats"
          options={KIND_OPTIONS}
          value={kind}
          onChange={setKind}
          hint="A fortnight is every 2 weeks on one weekday — anchored to the start date, not to the calendar."
        />

        {kind === 'INTERVAL_DAYS' ? (
          <TextField
            label="Every"
            numeric
            suffix="days"
            value={intervalDaysText}
            onChange={setIntervalDaysText}
            hint="Whole days between shifts, counted from the start date."
          />
        ) : (
          <>
            <SelectField
              label="On"
              options={WEEKDAY_OPTIONS}
              value={weekdayText}
              onChange={setWeekdayText}
            />
            <SelectField
              label="Every"
              options={WEEKS_INTERVAL_OPTIONS}
              value={weeksIntervalText}
              onChange={setWeeksIntervalText}
              hint="Every 2 weeks is fortnightly. Two rules with different start dates can sit on opposite fortnights, and that is correct."
            />
          </>
        )}

        <SegmentedControl label="Shift" options={CODE_OPTIONS} value={code} onChange={setCode} />

        <TextField
          label="Start date"
          value={startDateText}
          onChange={setStartDateText}
          hint="First possible date, as YYYY-MM-DD — the pattern is counted from it."
        />
        <TextField
          label="End date (optional)"
          value={endDateText}
          onChange={setEndDateText}
          hint="Last possible date, as YYYY-MM-DD. Leave empty for no end."
        />

        <Button block onClick={handleSave}>
          Save rule
        </Button>
        {isEdit ? (
          <Button variant="secondary" block onClick={handleDelete}>
            Delete rule
          </Button>
        ) : null}
      </div>
    </Sheet>
  )
}
